import React, { useEffect, useState } from "react";
import { Facebook, CheckCircle2, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

/**
 * SocialPreviewCard — shows how the article will look when shared on Facebook /
 * Twitter and validates the image (HTTP 200, MIME correct, dimensions).
 *
 * Props:
 *   title, description, image (URL), url (canonical), siteHost (e.g. "surprimicia.com.ar")
 */
export default function SocialPreviewCard({ title, description, image, url, siteHost = "surprimicia.com.ar" }) {
  const [check, setCheck] = useState({ loading: true, ok: false, warnings: [], mime: null, w: null, h: null, status: null });

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      const warnings = [];
      if (!image) {
        if (!cancelled) setCheck({ loading: false, ok: false, warnings: ["No hay imagen principal cargada"], status: null, mime: null, w: null, h: null });
        return;
      }
      if (!/^https?:\/\//.test(image)) warnings.push("La URL no es absoluta (debe empezar con https://)");
      if (image.startsWith("http://")) warnings.push("La URL no es HTTPS");
      if (!title) warnings.push("Falta el título");
      if (!description) warnings.push("Falta la bajada/descripción");

      // Try to load the image in a hidden Image() to check reachability + real dimensions.
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.referrerPolicy = "no-referrer";
      const dims = await new Promise((resolve) => {
        const timer = setTimeout(() => resolve({ ok: false, reason: "timeout" }), 8000);
        img.onload = () => { clearTimeout(timer); resolve({ ok: true, w: img.naturalWidth, h: img.naturalHeight }); };
        img.onerror = () => { clearTimeout(timer); resolve({ ok: false, reason: "load-error" }); };
        img.src = image;
      });

      let mime = null;
      const low = (image || "").toLowerCase().split("?")[0];
      if (low.endsWith(".jpg") || low.endsWith(".jpeg")) mime = "image/jpeg";
      else if (low.endsWith(".png")) mime = "image/png";
      else if (low.endsWith(".webp")) mime = "image/webp";
      else if (low.endsWith(".gif")) mime = "image/gif";
      else warnings.push("Extensión de imagen desconocida (recomendado: .jpg, .png, .webp)");

      if (!dims.ok) {
        warnings.push(dims.reason === "timeout" ? "La imagen tarda demasiado en cargar" : "La imagen no responde o requiere autenticación");
      } else {
        if (dims.w < 600 || dims.h < 315) warnings.push(`Imagen chica (${dims.w}×${dims.h}). Ideal: 1200×630`);
        else if (dims.w < 1200) warnings.push(`Imagen menor a 1200px de ancho (${dims.w}×${dims.h}). Facebook prefiere 1200×630`);
      }

      if (!cancelled) {
        setCheck({
          loading: false,
          ok: warnings.length === 0,
          warnings,
          mime,
          w: dims.w || null,
          h: dims.h || null,
          status: dims.ok ? 200 : "error",
        });
      }
    }
    setCheck((c) => ({ ...c, loading: true }));
    validate();
    return () => { cancelled = true; };
  }, [image, title, description]);

  const debuggerUrl = url ? `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(url)}` : null;

  return (
    <div className="bg-white border border-softblue p-5 space-y-4" data-testid="social-preview-card">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-lg font-bold text-blueblack flex items-center gap-2">
          <Facebook className="w-4 h-4 text-turquoise" /> Vista previa para redes
        </h3>
        {check.loading ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-textgray uppercase tracking-widest">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Validando…
          </span>
        ) : check.ok ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-600 font-bold uppercase tracking-widest">
            <CheckCircle2 className="w-3.5 h-3.5" /> Vista previa válida
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-600 font-bold uppercase tracking-widest">
            <AlertTriangle className="w-3.5 h-3.5" /> Problemas
          </span>
        )}
      </div>

      {/* Facebook-style card mock */}
      <div className="border border-softblue overflow-hidden">
        <div className="bg-blueblack aspect-[1200/630] flex items-center justify-center">
          {image ? (
            <img src={image} alt={title || "vista previa"} className="w-full h-full object-cover" />
          ) : (
            <div className="text-textgray text-sm">Sin imagen principal</div>
          )}
        </div>
        <div className="bg-lightgray p-3 space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-textgray">{siteHost}</div>
          <div className="font-bold text-blueblack text-sm leading-snug line-clamp-2">{title || "(Sin título)"}</div>
          <div className="text-xs text-textgray leading-snug line-clamp-2">{description || "(Sin bajada)"}</div>
        </div>
      </div>

      {/* Diagnostics */}
      {!check.loading && (
        <div className="text-[11px] text-textgray space-y-1">
          {check.status === 200 && (
            <div>✓ Imagen HTTP 200 · {check.mime || "MIME desconocido"} · {check.w}×{check.h}px</div>
          )}
          {check.warnings.map((w, i) => (
            <div key={i} className="text-amber-700">⚠ {w}</div>
          ))}
        </div>
      )}

      {debuggerUrl && (
        <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-softblue">
          <a
            href={debuggerUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="facebook-debugger-link"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-nightblue hover:text-turquoise"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Abrir Facebook Sharing Debugger
          </a>
          <span className="text-[10px] text-textgray">
            (Pedile a Facebook &quot;Scrape Again&quot; después de publicar/editar)
          </span>
        </div>
      )}
    </div>
  );
}
